import {useMemo, useRef, useState} from "react";
import {Alert, Button, CircularProgress, Grid, Input, Typography} from "@mui/material";
import {Trans, useTranslation} from "react-i18next";
import {Dialog} from "../base/Dialog";
import {UserIcon} from "../icons/UserIcon";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {createBulkUserInvitations} from "../../services/invitationService";
import {getResourceErrorMessage} from "../../utils/resourceUtils";
import {swallowException} from "../../utils/appUtils";
import {downloadCsv} from "../../utils/downloadUtils";
import {CloudUploadIcon} from "../icons/CloudUploadIcon";

interface Props {
    companyId: string;
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const CompanyMemberBulkInviteDialog: React.FC<Props> = ({companyId, open, onClose}) => {
    const {t} = useTranslation();
    const [validationError, setValidationError] = useState<string>();
    const [invitationsCreated, setInvitationsCreated] = useState<number>();
    const [invitationsSkipped, setInvitationsSkipped] = useState<number>();
    const [isSuccessful, setIsSuccessful] = useState(false);
    const invitationManagement = useResourceManagement({createBulkUserInvitations});
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const dialogError = useMemo(() => {
        return validationError ?? getResourceErrorMessage(invitationManagement.resource);
    }, [invitationManagement.resource, validationError]);

    const downloadExampleCsv = () => {
        const rows = [
            ["email", "companyId", "role"],
            ["example@hydrosat.com", companyId, "EDITOR"],
        ];
        downloadCsv(rows, "example.csv");
    };

    const uploadCsv = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (!file.name.toLowerCase().endsWith(".csv")) {
            setValidationError(t("dialogs.member_bulk_invite.invalid_file"));
            setInvitationsCreated(undefined);
            setInvitationsSkipped(undefined);
            setIsSuccessful(false);
            event.target.value = "";
            return;
        }

        setValidationError(undefined);
        setInvitationsCreated(undefined);
        setInvitationsSkipped(undefined);
        setIsSuccessful(false);
        invitationManagement.reset();

        invitationManagement
            .createBulkUserInvitations(companyId, file)
            .then((response) => {
                setInvitationsCreated(response.invitationsCreated);
                setInvitationsSkipped(response.invitationsSkipped);
                setIsSuccessful(true);
            })
            .catch(swallowException);

        event.target.value = "";
    };

    const handleClose = () => {
        onClose(isSuccessful);
    };

    return (
        <Dialog
            width="50rem"
            title={t("dialogs.member_bulk_invite.title")}
            titleIcon={
                <UserIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            open={open}
            error={dialogError}
            isLoading={invitationManagement.resource.state === "loading"}
            cancelTitle={isSuccessful ? t("dialogs.actions.close") : t("dialogs.actions.cancel")}
            onClose={handleClose}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography color="text.secondary">
                        <Trans
                            i18nKey="dialogs.member_bulk_invite.description"
                            components={{
                                button: (
                                    <Typography
                                        component="span"
                                        onClick={() => {
                                            downloadExampleCsv();
                                        }}
                                        sx={{
                                            color: "primary.main",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                        }}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        onClick={uploadCsv}
                        variant="outlined"
                        disabled={invitationManagement.resource.state === "loading"}
                        sx={{
                            minWidth: "100px",
                        }}
                    >
                        {invitationManagement.resource.state === "loading" ? (
                            <CircularProgress size="1.25rem" />
                        ) : (
                            <CloudUploadIcon
                                sx={{
                                    stroke: "#404DB8",
                                    width: "1.25rem",
                                    height: "1.25rem",
                                }}
                            />
                        )}
                        &nbsp; {t("dialogs.member_bulk_invite.upload_csv")}
                    </Button>
                    <Input
                        type="file"
                        inputRef={fileInputRef}
                        onChange={handleFileChange}
                        inputProps={{accept: ".csv"}}
                        style={{display: "none"}}
                    />
                </Grid>
                {invitationsCreated !== undefined && invitationsSkipped !== undefined && (
                    <Grid item xs={12}>
                        <Alert severity="success">
                            {t("dialogs.member_bulk_invite.success", {
                                created: invitationsCreated,
                                skipped: invitationsSkipped,
                            })}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Dialog>
    );
};
